import type { ReactNode } from 'react';
import { EmptyState } from '../../primitives/EmptyState.js';
import { StateIllustration } from '../StateIllustration.js';
import styles from './DataTable.module.css';

export type DataTableEmptyStateProps = {
  emptyMessage?: string;
  emptyDescription?: ReactNode;
  emptyAction?: ReactNode;
  emptyVariant?: 'default' | 'filter' | 'resource';
  /** Tighter empty tbody for drawers / flyouts — no 10rem min-height stretch. */
  compactEmpty?: boolean;
  /** Centered in tbody row (default). `false` for stacked mobile layout. */
  overlay?: boolean;
};

export function DataTableEmptyState({
  emptyMessage = 'Aucune donnée.',
  emptyDescription,
  emptyAction,
  emptyVariant = 'default',
  compactEmpty = false,
  overlay = true,
}: DataTableEmptyStateProps) {
  const title = emptyVariant === 'filter' ? 'Aucun résultat pour ces filtres' : emptyMessage;
  const description =
    emptyDescription ??
    (emptyVariant === 'filter' ? 'Affinez la recherche ou réinitialisez les filtres.' : undefined);
  const illustrationVariant = emptyVariant === 'filter' ? 'search' : 'no-data';

  const content = (
    <EmptyState
      density="compact"
      title={title}
      description={description}
      action={emptyAction}
      illustration={<StateIllustration variant={illustrationVariant} size="sm" tone="soft" />}
    />
  );

  if (!overlay) {
    return (
      <div className={styles.emptyStacked} role="status">
        {content}
      </div>
    );
  }

  return (
    <div className={compactEmpty ? styles.emptyInlineCompact : styles.emptyInline} role="status">
      {content}
    </div>
  );
}
